export type OnboardingStepKind = 'inventory' | 'practice' | 'report' | 'qora';

// Where a step lives in the sidebar. Top-level modules are a single nav item;
// grouped modules need the group expanded, then the child link clicked.
export type NavTarget = { item: string } | { groupToggle: string; child: string };

export interface OnboardingStepDef {
  step: number; // 1..8
  module: string; // display label
  route: string; // where the action happens
  kind: OnboardingStepKind;
  real: boolean; // step 1 & 8 act on real data
  nav: NavTarget; // how to reach the screen from the menu
  navInstruction: string; // copy while guiding the user to the screen
  instruction: string; // copy once on the screen
  hint?: string; // optional quiet secondary line under the instruction (lock-mode steps)
  actionTarget?: string; // real on-screen control to spotlight (when applicable)
}

const NAV = {
  inventory: { item: '[data-test-id="sidebar-nav-item-inventory"]' },
  qora: { item: '[data-test-id="sidebar-nav-item-qora-assistant"]' },
  purchasing: {
    groupToggle: '[data-test-id="sidebar-group-toggle-purchasing"]',
    child: '[data-test-id="sidebar-nav-child-purchase-order"]',
  },
  production: {
    groupToggle: '[data-test-id="sidebar-group-toggle-production"]',
    child: '[data-test-id="sidebar-nav-child-work-order"]',
  },
  qualityControl: {
    groupToggle: '[data-test-id="sidebar-group-toggle-quality-control"]',
    child: '[data-test-id="sidebar-nav-child-cupping-session"]',
  },
  selling: {
    groupToggle: '[data-test-id="sidebar-group-toggle-selling"]',
    child: '[data-test-id="sidebar-nav-child-sales-order"]',
  },
  report: {
    groupToggle: '[data-test-id="sidebar-group-toggle-report"]',
    child: '[data-test-id="sidebar-nav-child-general-report"]',
  },
} as const;

export const ONBOARDING_STEPS: OnboardingStepDef[] = [
  {
    step: 1,
    module: 'Inventory',
    route: '/inventory',
    kind: 'inventory',
    real: true,
    nav: NAV.inventory,
    navInstruction: 'Open Inventory from the menu — this is where your real stock lives.',
    instruction: 'Click “Add New Green Bean Item” to enter your first real stock item.',
    actionTarget: '[data-test-id="inventory-add-item-button"]',
  },
  {
    step: 2,
    module: 'Purchasing',
    route: '/purchase-orders',
    kind: 'practice',
    real: false,
    nav: NAV.purchasing,
    navInstruction: 'Open Purchasing, then Purchase Order, from the menu.',
    instruction:
      "This is where you buy green beans. Click Create Purchase Order to place your first practice order.",
    actionTarget: '[data-test-id="po-create-button"]',
  },
  {
    step: 3,
    module: 'Production',
    route: '/work-orders',
    kind: 'practice',
    real: false,
    nav: NAV.production,
    navInstruction: 'Open Production, then Work Order, from the menu.',
    instruction:
      'Roasting happens here. Click Create Work Order to roast your first practice batch.',
    hint: 'Sample roaster & machine — add your own later in Settings → Roaster / Machine.',
    actionTarget: '[data-test-id="wo-create-button"]',
  },
  {
    step: 4,
    module: 'Quality Control',
    route: '/cupping-sessions',
    kind: 'practice',
    real: false,
    nav: NAV.qualityControl,
    navInstruction: 'Open Quality Control, then Cupping Session, from the menu.',
    instruction:
      'Score your roasts here. Click New Cupping Session to start your first practice cupping.',
    hint: 'Two sample roasts are ready — pick both to compare in the cupping.',
    actionTarget: '[data-test-id="cupping-session-new-button"]',
  },
  {
    step: 5,
    module: 'Finished Goods',
    route: '/work-orders',
    kind: 'practice',
    real: false,
    nav: NAV.production,
    navInstruction: 'Open Production, then Work Order, from the menu.',
    instruction:
      'Pack your roast into a sellable product here. We open the Packing tab — pick your sample roast and save to create a practice finished good.',
    hint: 'Packing turns roasted bulk into finished goods you can sell.',
    actionTarget: '[data-test-id="wo-create-button"]',
  },
  {
    step: 6,
    module: 'Selling',
    route: '/sales-orders',
    kind: 'practice',
    real: false,
    nav: NAV.selling,
    navInstruction: 'Open Selling, then Sales Order, from the menu.',
    instruction:
      'Sell finished coffee here. Create a practice sale of the finished good you just packed, then walk it Draft → Sent → Delivered.',
    actionTarget: '[data-test-id="so-create-button"]',
  },
  {
    step: 7,
    module: 'Report',
    route: '/reports/general',
    kind: 'report',
    real: false,
    nav: NAV.report,
    navInstruction: 'Open Report, then General Report, from the menu.',
    instruction:
      "Here's the report your practice chain produced — switch tabs to see roasting, inventory, sales and customers. It's a preview of your real reporting once you're live. Click Next.",
    hint: 'Your practice purchase, roast, cupping, packing and sale all feed these numbers.',
    actionTarget: '[data-test-id="report-mode-selector"]',
  },
  {
    step: 8,
    module: 'Qora',
    route: '/qora-assistant',
    kind: 'qora',
    real: true,
    nav: NAV.qora,
    navInstruction: 'Open Qora Assistant from the menu.',
    instruction:
      'Ask Qora about your real inventory — e.g. how much of a lot you have left — then finish the tour.',
    actionTarget: '[data-test-id="chat-input-container"]',
  },
];

export const TOTAL_STEPS = 8;

/**
 * True when the tour is mid-step but the user hasn't reached that step's screen
 * yet — the "navigate to it via the sidebar menu" phase. Mirrors the overlay's
 * `navPhase = !onRightScreen` derivation so both agree on when the menu is the
 * thing to click. On mobile the sidebar is an off-canvas drawer, so the layout
 * opens it during this phase (see DashboardLayout) — otherwise the highlighted
 * menu item sits off-screen and the tour cannot advance.
 */
export function isOnboardingNavPhase(
  currentStep: number | undefined,
  pathname: string,
): boolean {
  const def = ONBOARDING_STEPS.find((s) => s.step === currentStep);
  if (!def) return false;
  return !(pathname === def.route || pathname.startsWith(def.route + '/'));
}
